/**
 * PURPOSE: Configurar cómo se consolidan instituciones en el medallero general.
 * RESPONSIBILITIES: Vincular instituciones participantes con una institución principal y guardar el mapeo por evento.
 * COLLABORATORS: hayllisV2MedalService.js y MedalConsolidationPreview.jsx.
 * ROUTE: src/area/MedalTableArea/MedalConsolidationEditor.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaLink, FaSave, FaSearch, FaSyncAlt } from "react-icons/fa";
import {
  getMedalConsolidationEditor,
  searchMedalConsolidationInstitutions,
  updateMedalConsolidationBulk,
} from "../../services/hayllisV2MedalService.js";
import MedalConsolidationPreview from "./MedalConsolidationPreview.jsx";

const mergeCandidates = (current, incoming) => {
  const map = new Map((current || []).map((item) => [Number(item.id), item]));
  (incoming || []).forEach((item) => map.set(Number(item.id), item));
  return Array.from(map.values()).sort((a, b) => String(a.name).localeCompare(String(b.name), "es"));
};

export default function MedalConsolidationEditor() {
  const { idevent } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [sourceSummary, setSourceSummary] = useState([]);

  const load = useCallback(async () => {
    if (!idevent) return;
    setLoading(true);
    try {
      const payload = await getMedalConsolidationEditor({ idevent });
      const nextRows = (payload?.institutions || []).map((item) => ({
        source: item.source,
        targetInstitutionId: item.target?.id ? String(item.target.id) : "",
      }));
      setRows(nextRows);
      setCandidates(mergeCandidates(payload?.candidates || [], nextRows.map((item) => item.source)));
      setSourceSummary(payload?.sourceSummary || []);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo cargar la consolidación general");
    } finally {
      setLoading(false);
    }
  }, [idevent]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const found = await searchMedalConsolidationInstitutions({ idevent, search: search.trim() });
      setCandidates((current) => mergeCandidates(current, found));
      if (!found.length) toast("No se encontraron instituciones");
    } catch (error) {
      console.error(error);
      toast.error("No se pudo buscar instituciones");
    } finally {
      setSearching(false);
    }
  };

  const updateTarget = (sourceId, targetId) => {
    setRows((current) => current.map((row) => Number(row.source.id) === Number(sourceId) ? { ...row, targetInstitutionId: targetId } : row));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = await updateMedalConsolidationBulk({
        idevent,
        items: rows.map((row) => ({
          sourceInstitutionId: Number(row.source.id),
          targetInstitutionId: row.targetInstitutionId ? Number(row.targetInstitutionId) : null,
        })),
      });
      toast.success("Consolidación general guardada");
      const nextRows = (payload?.institutions || []).map((item) => ({ source: item.source, targetInstitutionId: item.target?.id ? String(item.target.id) : "" }));
      setRows(nextRows);
      setCandidates((current) => mergeCandidates(current, payload?.candidates || []));
      setSourceSummary(payload?.sourceSummary || []);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error?.response?.data?.msg || "No se pudo guardar la consolidación");
    } finally {
      setSaving(false);
    }
  };

  const candidateOptions = useMemo(() => candidates || [], [candidates]);

  return (
    <section className="grid gap-6">
      <div className="rounded-2xl border border-blue-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white"><FaLink /></span>
            <div><h2 className="text-2xl font-bold text-gray-900">Consolidación general</h2><p className="text-sm text-gray-500">Agrupa instituciones solo en el resumen general. El llenado por deporte conserva el detalle original.</p></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={load} disabled={loading || saving} className="inline-flex items-center gap-2 rounded-xl bg-gray-200 px-4 py-3 text-sm font-bold text-gray-700"><FaSyncAlt className={loading ? "animate-spin" : ""} />Recargar</button>
            <button type="button" onClick={handleSave} disabled={loading || saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"><FaSave />{saving ? "Guardando..." : "Guardar vínculos"}</button>
          </div>
        </div>
        <div className="mt-5 flex max-w-xl gap-2">
          <input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && handleSearch()} placeholder="Buscar otra institución principal" className="min-w-0 flex-1 rounded-xl border border-gray-300 px-4 py-3 text-sm" />
          <button type="button" onClick={handleSearch} disabled={searching || !search.trim()} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-3 text-sm font-bold"><FaSearch />{searching ? "Buscando..." : "Buscar"}</button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900">Vínculos por institución</h3>
        <p className="mt-1 text-sm text-gray-500">“Se representa a sí misma” mantiene una fila independiente en el medallero general.</p>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-[760px] w-full text-left">
            <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500"><tr><th className="px-4 py-3">Institución participante</th><th className="px-4 py-3">Mostrar en el general como</th><th className="px-4 py-3">Estado</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.source.id}>
                  <td className="px-4 py-3"><div className="font-bold text-gray-900">{row.source.name}</div><div className="text-xs font-semibold uppercase text-gray-400">{row.source.abbreviation}</div></td>
                  <td className="px-4 py-3">
                    <select value={row.targetInstitutionId} onChange={(event) => updateTarget(row.source.id, event.target.value)} disabled={loading || saving} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-semibold">
                      <option value="">Se representa a sí misma</option>
                      {candidateOptions.filter((item) => Number(item.id) !== Number(row.source.id)).map((item) => <option key={item.id} value={item.id}>{item.name} {item.abbreviation ? `(${item.abbreviation})` : ""}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-3 py-1 text-xs font-bold ${row.targetInstitutionId ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{row.targetInstitutionId ? "Vinculada" : "Principal"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MedalConsolidationPreview rows={rows} sourceSummary={sourceSummary} candidates={candidates} />
    </section>
  );
}
